import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import ReactECharts from "echarts-for-react";
import { getProductionsData } from "service/productionData";
import { getArpsForecast } from "service/dcaAnalysis";
import {
  selectAssetById,
  selectWellByAssetAndId,
} from "features/asset/assetSelectorSlice";

import * as dfd from "danfojs";

const defaultState = {
  title: {
    show: true,
    text: "",
    textStyle: {
      color: "#bbb",
      fontSize: 15,
    },
  },
  legend: {
    data: ["Oil"], //OK
    textStyle: {
      color: "#ccc",
    },
  },
  toolbox: {
    left: "center",
    itemSize: 18,
    top: 15,
    feature: {
      dataZoom: {
        yAxisIndex: "none",
      },
      restore: {},
      saveAsImage: { type: "png" },
    },
  },
  grid: {
    left: "3%",
    right: "4%",
    bottom: "3%",
    containLabel: true,
  },
  xAxis: {
    type: "category",
    data: ["1970-1-1"],
  },
  yAxis: [
    {
      type: "value",
      show: true,
      splitLine: {
        show: true,
        lineStyle: {
          opacity: "0.2",
          color: ["#fff"],
        },
      },
      name: "",
      nameTextStyle: {
        color: "#aaa", // Todo color
        nameLocation: "middle",
      },
    },
  ],
  series: [
    {
      name: "Oil",
      type: "line",
      data: [0],
    },
  ],
  tooltip: {
    trigger: "axis",
  },
};

const updateLiquidRate = (source, result) => {
  let dataDF = null;
  for (let sourceName of source) {
    let series = result[sourceName];
    let sourceDF = new dfd.DataFrame(series, { columns: ["date", sourceName] });
    if (dataDF == null) {
      dataDF = sourceDF;
    } else {
      dataDF = dfd.merge({
        left: dataDF,
        right: sourceDF,
        on: ["date"],
        how: "outer",
      });
    }
  }
  dataDF.fillNa(0, { inplace: true });
  let liquidDF = dataDF
    .loc({ columns: source })
    .apply((row) => row.reduce((a, b) => a + b, 0), { axis: 1 });
  let dates = dataDF["date"].values;
  let values = liquidDF.values;
  let zip = (x, y) =>
    Array.from(Array(Math.max(x.length, y.length)), (_, i) => [x[i], y[i]]);
  let data = zip(dates, values);
  return data;
};

const updateCumulativeData = (source, result) => {
  let series = result[source[0]]; // only one
  let df = new dfd.DataFrame(series, { columns: ["date", source[0]] });
  // df["date"] = df["date"].astype("datetime64");
  let cumsum = df[source[0]].cumSum({ axis: 0 });
  df.addColumn("cumsum", cumsum, { inplace: true }); //{ column: "cumsum", value: cumsum }
  df.drop({ columns: source[0], inplace: true });
  // let data = dfd.toJSON(df);
  let data = df.values;
  return data;
};

const updateCumulativeLiquid = (source, result) => {
  if (source.length < 2 || result == null || result[source[0]] == null) {
    return;
  }

  let dataDF = null;
  for (let sourceName of source) {
    let series = result[sourceName];
    let sourceDF = new dfd.DataFrame(series, { columns: ["date", sourceName] });
    if (dataDF == null) {
      dataDF = sourceDF;
    } else {
      dataDF = dfd.merge({
        left: dataDF,
        right: sourceDF,
        on: ["date"],
        how: "outer",
      });
    }
  }
  dataDF.fillNa(0, { inplace: true });
  let liquidDF = dataDF
    .loc({ columns: source })
    .apply((row) => row.reduce((a, b) => a + b, 0), { axis: 1 });
  dataDF.addColumn("liquid", liquidDF, { inplace: true });
  dataDF.drop({ columns: source, inplace: true });
  let cumsum = dataDF["liquid"].cumSum({ axis: 0 });
  dataDF.addColumn("cumsum", cumsum, { inplace: true });
  dataDF.drop({ columns: "liquid", inplace: true });
  let data = dataDF.values;
  return data;
};

const updateOilGasRatio = (source, result) => {
  let oilSeries = result["oil_prd_rate"];
  let gasSeries = result["gas_prd_rate"];
  let oilDF = new dfd.DataFrame(oilSeries, {
    columns: ["date", "oil_prd_rate"],
  });
  let gasDF = new dfd.DataFrame(gasSeries, {
    columns: ["date", "gas_prd_rate"],
  });
  let df = dfd.merge({ left: oilDF, right: gasDF, on: ["date"], how: "outer" });
  df.fillNa(0, { inplace: true });
  let ratio = df["gas_prd_rate"].div(df["oil_prd_rate"]);
  df.addColumn("ratio", ratio, { inplace: true });
  df.drop({ columns: ["oil_prd_rate", "gas_prd_rate"], inplace: true });
  df.fillNa(0, { inplace: true });
  return df.values;
};

const updateWaterCut = (source, result) => {
  let oilSeries = result["oil_prd_rate"];
  let waterSeries = result["water_prd_rate"];
  // for (let dateStr of Object.keys(oilSeries)) {
  //   let sum = oilSeries[dateStr] + waterSeries[dateStr];
  //   data[dateStr] = sum ? waterSeries[dateStr] / sum : 0;
  // }
  let oilDF = new dfd.DataFrame(oilSeries, {
    columns: ["date", "oil_prd_rate"],
  });
  let waterDF = new dfd.DataFrame(waterSeries, {
    columns: ["date", "water_prd_rate"],
  });
  let df = dfd.merge({
    left: oilDF,
    right: waterDF,
    on: ["date"],
    how: "outer",
  });
  df.fillNa(0, { inplace: true });
  let liquidDF = df
    .loc({ columns: source })
    .apply((row) => row.reduce((a, b) => a + b, 0), { axis: 1 });
  df.addColumn("sum", liquidDF, { inplace: true });
  let ratio = df["water_prd_rate"].div(df["sum"]);
  df.addColumn("ratio", ratio, { inplace: true });
  df.print();
  df.drop({
    columns: ["oil_prd_rate", "water_prd_rate", "sum"],
    inplace: true,
  });
  df.fillNa(0, { inplace: true });
  return df.values;
};

const PRODUCT_DATA_SOURCE_HANDLER = {
  oil_prd_rate: { onlyValue: true, unit: "bbl/m" },
  gas_prd_rate: { onlyValue: true, unit: "Mscf/m" },
  water_prd_rate: { onlyValue: true, unit: "bbl/m" },
  power_consumption: { onlyValue: true, unit: "kWh" },
  liquid_rate: {
    source: ["oil_prd_rate", "water_prd_rate"],
    handler: updateLiquidRate,
    unit: "bbl/m",
  },
  cumulative_oil: {
    source: ["oil_prd_rate"],
    handler: updateCumulativeData,
    onlyValue: true,
    unit: "bbl",
  },
  cumulative_gas: {
    source: ["gas_prd_rate"],
    handler: updateCumulativeData,
    onlyValue: true,
    unit: "Mscf",
  },
  cumulative_water: {
    source: ["water_prd_rate"],
    handler: updateCumulativeData,
    onlyValue: true,
    unit: "bbl",
  },
  cumulative_power_consumption: {
    source: ["power_consumption"],
    handler: updateCumulativeData,
    onlyValue: true,
    unit: "kWh",
  },
  cumulative_liquid: {
    source: ["oil_prd_rate", "water_prd_rate"],
    handler: updateCumulativeLiquid,
    unit: "bbl",
  },
  oil_gas_ratio: {
    source: ["oil_prd_rate", "gas_prd_rate"],
    handler: updateOilGasRatio,
    unit: "",
  },
  water_cut: {
    source: ["oil_prd_rate", "water_prd_rate"],
    handler: updateWaterCut,
    unit: "",
  },
};

const COLORS = [
  "#5470c6",
  "#91cc75",
  "#fac858",
  "#ee6666",
  "#73c0de",
  "#3ba272",
  "#fc8452",
  "#9a60b4",
  "#ea7ccc",
];

// AV-B1: classify each product into an axis "group" by its phase/unit so that
// liquids (bbl) always share the LEFT axis and gas (Mscf) the RIGHT axis,
// instead of giving every selected series its own axis by selection order.
// Returns one of: "liquid" | "gas" | "ratio" | "power".
const getAxisGroup = (productValue) => {
  const unit = (PRODUCT_DATA_SOURCE_HANDLER[productValue]?.unit || "")
    .toLowerCase();
  if (unit.includes("mscf")) return "gas";
  if (unit.includes("bbl")) return "liquid";
  if (unit.includes("kwh")) return "power";
  return "ratio"; // GOR / water-cut and other dimensionless series
};

// Axis layout: liquid -> left, everything else -> right (stacked by offset).
const AXIS_GROUP_META = {
  liquid: { position: "left", side: "left", color: "#91cc75", label: "Liquid (bbl)" },
  gas: { position: "right", side: "right", color: "#ee6666", label: "Gas (Mscf)" },
  ratio: { position: "right", side: "right", color: "#fac858", label: "Ratio" },
  power: { position: "right", side: "right", color: "#73c0de", label: "Power (kWh)" },
};

// AV-B2: a log axis cannot plot 0 / negative values; map them to null so the
// point becomes a gap instead of crashing/clamping the whole axis.
const sanitizeForLog = (value, logScale) => {
  if (!logScale) return value;
  return value > 0 ? value : null;
};

// AV-B3: visual style for the Arps forecast overlay — dashed + distinct color
// so it reads clearly as a projection, not measured history.
const FORECAST_COLOR = "#ff7f0e";

// AV-B3: format the forecast date to the same x-axis granularity as history.
// Daily history uses YYYY-MM-DD categories; monthly uses YYYY-MM. We slice the
// forecast date the same way so the overlay lands on the SAME category axis.
const forecastDateKey = (dateStr, period) =>
  period === 1 ? dateStr.slice(0, 10) : dateStr.slice(0, 7);

// AV-B3: Arps decline classification by hyperbolic exponent b (PE acceptance:
// b range must be legal & labelled). b=0 exponential, 0<b<1 hyperbolic, b=1
// harmonic. Anything outside [0,1] is flagged as out-of-range for the analyst.
const arpsModelLabel = (b) => {
  if (b == null || isNaN(b)) return "—";
  if (b < 0 || b > 1) return `b=${(+b).toFixed(2)} (out of range)`;
  if (b === 0) return "Exponential (b=0)";
  if (b === 1) return "Harmonic (b=1)";
  return `Hyperbolic (b=${(+b).toFixed(2)})`;
};

let dataCache = {};

const HistoricalAAsset2 = (props) => {
  const {
    wellId,
    assetId,
    models,
    period,
    logScale = false,
    // AV-B3: when true, overlay the Arps decline forecast on the history curve.
    showForecast = false,
  } = props;

  const asset = useSelector((state) => selectAssetById(state, assetId));
  const well = useSelector((state) =>
    selectWellByAssetAndId(state, assetId, wellId)
  );
  const { loadingIconActions } = props;
  const [showLoadingIcon, hideLoadingIcon] = loadingIconActions || [];

  // AV-B3: Arps fit result { b, Di, qi, EUR, unit, forecast:[{date,rate}] }.
  // null = not loaded / endpoint not ready -> overlay is simply not drawn.
  const [arps, setArps] = useState(null);

  let titleText = "";
  if (well) {
    titleText = well.well_name;
  } else if (asset) {
    titleText = asset.asset_name;
  }

  const [options, setOptions] = useState(defaultState);

  let legendArr = [],
    seriesValues = [],
    xAxisData = [],
    yAxisArr = [],
    // AV-B3: ECharts `graphic` elements for the Arps info box (b / Di / EUR).
    forecastGraphic = [];

  function getChartOptions() {
    const getAllProducts = (models) => {
      let products = [];
      models.forEach(({ value, name }) => {
        let modelProducts = PRODUCT_DATA_SOURCE_HANDLER[value].source || [
          value,
        ];
        modelProducts.forEach((p) => {
          if (!products.includes(p)) {
            products.push(p);
          }
        });
      });
      return products;
    };
    // get xAxis, set it
    const getXAxis = (data) => {
      let dates = data.map((item) => item[0]);
      if (period === 1) return dates;
      let months = [];
      dates.map((date) => {
        if (!months.includes(date.slice(0, 7))) months.push(date.slice(0, 7));
        return null;
      });
      return months;
    };
    // AV-B1: build one Y-axis per phase group (liquid LEFT / gas RIGHT / ...).
    // `rightCount` tracks how many right-side axes already exist so each new
    // right axis is offset outward and does not overlap the previous one.
    const getYAxis = (group, rightCount) => {
      const meta = AXIS_GROUP_META[group] || AXIS_GROUP_META.ratio;
      const isLeft = meta.side === "left";
      return {
        // AV-B2: switch to a logarithmic (semi-log) Y-axis when toggled on.
        type: logScale ? "log" : "value",
        logBase: 10,
        // keep the origin off the log axis; let it auto-fit positive data.
        min: logScale ? null : undefined,
        offset: isLeft ? 0 : rightCount * 55,
        position: meta.position,
        axisLine: {
          onZero: false,
          show: true,
          lineStyle: {
            color: meta.color,
          },
        },
        splitLine: {
          show: isLeft,
          lineStyle: {
            opacity: "0.2",
            color: ["#fff"],
          },
        },
        axisTick: {
          show: true,
          color: meta.color,
        },
        axisLabel: {
          show: true,
          interval: "auto",
          formatter: (value) => {
            let str = value;
            value >= 1000 && (str = value / 1000 + "K");
            value >= 1000 * 1000 && (str = value / 1000 / 1000 + "M");
            return str;
          },
        },
        // clear unit label so PE can tell liquid (bbl) from gas (Mscf) at a glance
        name: meta.label,
        nameTextStyle: {
          color: "#aaa",
          nameLocation: "middle",
        },
      };
    };

    // get values, clean(calc, get monthly)
    const dataHandled = (model, dataCache) => {
      let data = dataCache[model.value];
      let { handler, source } = PRODUCT_DATA_SOURCE_HANDLER[model.value];
      let result = data;

      handler && (result = handler(source, dataCache));
      return result;
    };

    // get values, clean(calc, get monthly)
    // `colorIndex` keeps the per-series color palette; `yAxisIndex` is the
    // phase-group axis (AV-B1) this series belongs to.
    const getValues = (model, data, colorIndex, yAxisIndex) => {
      if (period === 1) {
        return {
          name: model.name,
          type: "line",
          lineStyle: {
            color: COLORS[colorIndex % COLORS.length],
          },
          yAxisIndex: yAxisIndex || 0,
          data: data.map((item) => sanitizeForLog(item[1], logScale)),
        };
      }
      let monthsSet = new Set();
      let months = [];
      data.forEach((item) => {
        let month = item[0].slice(0, 7);
        if (!monthsSet.has(month)) {
          monthsSet.add(month);
          months.push(month);
        }
      });
      let monthData = {};
      data.forEach((item) => {
        let month = item[0].slice(0, 7);
        monthData[month] === undefined && (monthData[month] = 0);
        monthData[month] += item[1];
      });

      return {
        name: model.name,
        type: "line",
        yAxisIndex: yAxisIndex || 0,
        data: months.map((month) => sanitizeForLog(monthData[month], logScale)),
      };
    };

    const prepareOptions = () => {
      let tempOption = { ...options };
      tempOption.title.text = titleText;
      tempOption.legend.data = legendArr;
      tempOption.xAxis.data = xAxisData;
      tempOption.yAxis = yAxisArr;
      tempOption.series = seriesValues;
      // AV-B3: attach (or clear) the Arps annotation box.
      tempOption.graphic = forecastGraphic;
      return tempOption;
    };

    // AV-B3: which phase-axis group the forecast rate belongs to. Oil/water are
    // liquids (bbl, left axis); gas is Mscf (right axis). This keeps the
    // forecast on the SAME rate axis as the matching history series so the
    // overlay never mis-scales (PE acceptance: same-axis, no offset error).
    const forecastAxisGroup = forecastPhase === "gas" ? "gas" : "liquid";
    const forecastUnitHint =
      forecastAxisGroup === "gas" ? "Mscf" : "bbl";

    // AV-B3: continue the Arps forecast on the SAME category x-axis as history.
    // We (a) append forecast date categories after the last history date and
    // (b) build a forecast series left-padded with nulls for the history span,
    // so the two curves join seamlessly on one shared time & rate axis. The
    // overlay is dashed + a distinct color to read as a projection.
    const applyForecastOverlay = (groupAxisIndex, rightAxisCount) => {
      forecastGraphic = [];
      if (!showForecast || arps == null) return;
      const forecast = Array.isArray(arps.forecast) ? arps.forecast : [];
      if (forecast.length === 0 || xAxisData.length === 0) return;

      // Map forecast points to the history granularity; keep only dates that
      // fall strictly AFTER the last history category (continuation segment).
      const lastHist = xAxisData[xAxisData.length - 1];
      const seen = new Set(xAxisData);
      const fcKeys = [];
      const fcVals = [];
      forecast.forEach((pt) => {
        const key = forecastDateKey(String(pt.date), period);
        if (key <= lastHist || seen.has(key)) return;
        seen.add(key);
        fcKeys.push(key);
        fcVals.push(sanitizeForLog(pt.rate, logScale));
      });
      if (fcKeys.length === 0) return;

      // Extend the shared category axis with the forecast dates.
      const histLen = xAxisData.length;
      xAxisData = xAxisData.concat(fcKeys);

      // Resolve (or create) the rate axis the forecast attaches to.
      let axisIndex = groupAxisIndex[forecastAxisGroup];
      if (axisIndex === undefined) {
        const isLeft =
          (AXIS_GROUP_META[forecastAxisGroup] || AXIS_GROUP_META.ratio).side ===
          "left";
        yAxisArr.push(getYAxis(forecastAxisGroup, isLeft ? 0 : rightAxisCount));
        axisIndex = yAxisArr.length - 1;
      }

      // Forecast series: null for the whole history span, then forecast values,
      // so it lines up index-for-index with the extended x-axis.
      const seriesData = new Array(histLen).fill(null).concat(fcVals);
      seriesValues.push({
        name: "Arps Forecast",
        type: "line",
        yAxisIndex: axisIndex,
        smooth: false,
        symbol: "none",
        connectNulls: false,
        lineStyle: { color: FORECAST_COLOR, type: "dashed", width: 2 },
        itemStyle: { color: FORECAST_COLOR },
        data: seriesData,
      });
      if (!legendArr.includes("Arps Forecast")) legendArr.push("Arps Forecast");

      // Info box: b (with Arps model class), Di (decline rate + unit), EUR
      // (with unit). PE acceptance: b range legal, Di unit labelled, EUR dim.
      const b = arps.b;
      const diStr =
        arps.Di == null || isNaN(arps.Di)
          ? "—"
          : `${(+arps.Di).toFixed(4)} /${period === 1 ? "day" : "month"}`;
      const eurStr =
        arps.EUR == null || isNaN(arps.EUR)
          ? "—"
          : `${(+arps.EUR).toLocaleString()} ${arps.unit || forecastUnitHint}`;
      const lines = [
        "Arps Decline Forecast",
        `Model: ${arpsModelLabel(b)}`,
        `Di: ${diStr}`,
        `EUR: ${eurStr}`,
      ];
      forecastGraphic = [
        {
          type: "group",
          right: 12,
          top: 50,
          children: [
            {
              type: "rect",
              shape: { width: 188, height: 18 * lines.length + 12, r: 4 },
              style: {
                fill: "rgba(20,20,20,0.78)",
                stroke: FORECAST_COLOR,
                lineWidth: 1,
              },
            },
            {
              type: "text",
              left: 10,
              top: 8,
              style: {
                text: lines.join("\n"),
                fill: "#eee",
                fontSize: 11,
                lineHeight: 18,
              },
            },
          ],
        },
      ];
    };

    const getAllProductsData = (models) => {
      if (wellId == null && assetId == null) return;
      showLoadingIcon && showLoadingIcon();

      let allProducts = getAllProducts(models);

      let uwis = null;
      let asset_id = assetId;
      if (wellId != null) {
        uwis = [wellId];
        asset_id = null;
      }

      getProductionsData(
        uwis,
        asset_id,
        allProducts,
        null,
        null,
        true,
        (result) => {
          dataCache = result;
          // AV-B1: map each phase group to a single shared Y-axis index so
          // liquid series (oil/water/liquid, bbl) stack on ONE left axis and
          // gas (Mscf) on ONE right axis, regardless of selection order.
          let groupAxisIndex = {};
          let rightAxisCount = 0;
          models.forEach((model, index) => {
            let unsortedData = dataHandled(model, dataCache);
            hideLoadingIcon && hideLoadingIcon();
            if (unsortedData == null || unsortedData.length === 0) {
              legendArr = legendArr.filter((l) => l !== model.name);
              setOptions(defaultState);
              return;
            }
            let data = unsortedData.sort((a, b) => (a[0] > b[0] ? 1 : -1));
            // (Object.keys(unsortedData).sort((a, b) => a > b ? 1 : -1)).map(key => data[key] = unsortedData[key])
            !legendArr.includes(model.name) && legendArr.push(model.name);
            //
            xAxisData.length === 0 && (xAxisData = getXAxis(data));
            //
            let group = getAxisGroup(model.value);
            if (groupAxisIndex[group] === undefined) {
              const isLeft =
                (AXIS_GROUP_META[group] || AXIS_GROUP_META.ratio).side ===
                "left";
              yAxisArr.push(getYAxis(group, isLeft ? 0 : rightAxisCount));
              if (!isLeft) rightAxisCount += 1;
              groupAxisIndex[group] = yAxisArr.length - 1;
            }
            //
            seriesValues.push(
              getValues(model, data, index, groupAxisIndex[group])
            );
          });

          // AV-B3: overlay the Arps decline forecast (if loaded & toggled on).
          applyForecastOverlay(groupAxisIndex, rightAxisCount);

          setOptions(
            prepareOptions(legendArr, xAxisData, yAxisArr, seriesValues)
          );
        },
        (error) => {
          console.error("Error in fetching data.", error);
          hideLoadingIcon && hideLoadingIcon();
        }
      );
    };
    getAllProductsData(models);
  }

  // AV-B3: derive which phase to forecast from the selected series. The Arps
  // decline is fit per-phase; default to oil, but if only gas/water is shown,
  // forecast that phase so the overlay sits on the right rate axis.
  const forecastPhase = (() => {
    const vals = (models || []).map((m) => m.value);
    if (vals.some((v) => v.includes("oil"))) return "oil";
    if (vals.some((v) => v.includes("gas"))) return "gas";
    if (vals.some((v) => v.includes("water"))) return "water";
    return "oil";
  })();

  // AV-B3: fetch the Arps forecast whenever the entity/phase changes, but only
  // when the overlay is toggled on. Failures degrade to null (no overlay, no
  // error popup) via getArpsForecast's internal graceful-degradation catch.
  useEffect(() => {
    if (!showForecast || (wellId == null && assetId == null)) {
      setArps(null);
      return;
    }
    let cancelled = false;
    getArpsForecast(
      {
        wellId,
        assetId,
        assetName: asset?.asset_name,
        phase: forecastPhase,
      },
      (data) => {
        if (!cancelled) setArps(data || null);
      },
      () => {
        if (!cancelled) setArps(null);
      }
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForecast, wellId, assetId, forecastPhase]);

  useEffect(() => {
    getChartOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models, period, assetId, wellId, logScale, arps, showForecast]);

  return (
    <>
      <ReactECharts
        option={options}
        notMerge={true}
        onChartReady={(echarts) => { }}
        style={{ height: "100%" }}
      />
    </>
  );
};
export default HistoricalAAsset2;
